import React, { useEffect, useRef } from 'react';
import { Calendar, Trash2, X, TrendingUp, RotateCcw } from 'lucide-react';

interface CellContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  onDailyEntry: () => void;
  onClearOverride?: () => void;
  onClearDailyValues?: () => void;
  hasOverride: boolean;
  hasDailyValues: boolean;
  dailyMode?: 'blended' | 'legacy' | null; // blended = pinned days + live driver
  pinnedCount?: number;
  isEditable: boolean;
  accountName?: string;
  monthName?: string;
  isRevenueAccount?: boolean;
  onRevenueForecastSetup?: () => void;
  // Driver-level monthly overrides (rate stays live) on this month
  driverOverrideCount?: number;
  onClearDriverOverrides?: () => void;
}

export function CellContextMenu({
  isOpen,
  position,
  onClose,
  onDailyEntry,
  onClearOverride,
  onClearDailyValues,
  hasOverride,
  hasDailyValues,
  dailyMode = null,
  pinnedCount = 0,
  isEditable,
  accountName,
  monthName,
  isRevenueAccount,
  onRevenueForecastSetup,
  driverOverrideCount = 0,
  onClearDriverOverrides,
}: CellContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Adjust position to keep menu in viewport
  useEffect(() => {
    if (!isOpen || !menuRef.current) return;

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let adjustedX = position.x;
    let adjustedY = position.y;

    // Adjust if menu goes off right edge
    if (position.x + rect.width > viewport.width - 10) {
      adjustedX = viewport.width - rect.width - 10;
    }

    // Adjust if menu goes off bottom edge
    if (position.y + rect.height > viewport.height - 10) {
      adjustedY = viewport.height - rect.height - 10;
    }

    menu.style.left = `${adjustedX}px`;
    menu.style.top = `${adjustedY}px`;
  }, [isOpen, position]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[180px]"
      style={{ left: position.x, top: position.y }}
    >
      {/* Header with context info */}
      {(accountName || monthName) && (
        <div className="px-3 py-2 border-b border-slate-100">
          <p className="text-xs text-slate-500 truncate">
            {accountName && <span className="font-medium text-slate-700">{accountName}</span>}
            {accountName && monthName && <span className="mx-1">·</span>}
            {monthName && <span>{monthName}</span>}
          </p>
        </div>
      )}

      {/* Menu items */}
      <div className="py-1">
        {/* Revenue Forecast Setup - shown for revenue accounts */}
        {isRevenueAccount && onRevenueForecastSetup && (
          <button
            onClick={() => {
              onRevenueForecastSetup();
              onClose();
            }}
            className="w-full px-3 py-2 text-left flex items-center gap-2 text-sm hover:bg-orange-50 text-slate-700 font-medium"
          >
            <TrendingUp size={16} className="text-orange-500" />
            <span>Revenue Forecast Setup...</span>
          </button>
        )}

        {/* Divider between revenue setup and daily entry */}
        {isRevenueAccount && onRevenueForecastSetup && (
          <div className="my-1 border-t border-slate-100" />
        )}

        {/* Daily Entry - primary action */}
        <button
          onClick={() => {
            onDailyEntry();
            onClose();
          }}
          disabled={!isEditable}
          className={`
            w-full px-3 py-2 text-left flex items-center gap-2 text-sm
            ${isEditable
              ? 'hover:bg-blue-50 text-slate-700'
              : 'text-slate-400 cursor-not-allowed'
            }
          `}
        >
          <Calendar size={16} className={isEditable ? 'text-blue-600' : 'text-slate-300'} />
          <span>Daily Entry...</span>
          {hasDailyValues && (
            dailyMode === 'blended' ? (
              <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                {pinnedCount > 0 ? `${pinnedCount} pinned` : 'Pinned'}
              </span>
            ) : (
              <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                Static
              </span>
            )
          )}
        </button>

        {/* Divider */}
        {(hasOverride || hasDailyValues || driverOverrideCount > 0) && isEditable && (
          <div className="my-1 border-t border-slate-100" />
        )}

        {/* Revert driver-level (rate) overrides for this month */}
        {driverOverrideCount > 0 && isEditable && onClearDriverOverrides && (
          <button
            onClick={() => {
              onClearDriverOverrides();
              onClose();
            }}
            className="w-full px-3 py-2 text-left flex items-center gap-2 text-sm hover:bg-orange-50 text-slate-700"
          >
            <RotateCcw size={16} className="text-orange-500" />
            <span>Revert driver override{driverOverrideCount > 1 ? `s (${driverOverrideCount})` : ''}</span>
          </button>
        )}

        {/* Clear Override */}
        {hasOverride && isEditable && onClearOverride && (
          <button
            onClick={() => {
              onClearOverride();
              onClose();
            }}
            className="w-full px-3 py-2 text-left flex items-center gap-2 text-sm hover:bg-red-50 text-slate-700"
          >
            <X size={16} className="text-red-500" />
            <span>Clear Override</span>
          </button>
        )}

        {/* Clear Daily Values */}
        {hasDailyValues && isEditable && onClearDailyValues && (
          <button
            onClick={() => {
              onClearDailyValues();
              onClose();
            }}
            className="w-full px-3 py-2 text-left flex items-center gap-2 text-sm hover:bg-red-50 text-slate-700"
          >
            <Trash2 size={16} className="text-red-500" />
            <span>{dailyMode === 'blended' ? 'Clear Pins (back to live)' : 'Clear Daily Values'}</span>
          </button>
        )}
      </div>

      {/* Keyboard hint */}
      <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50">
        <p className="text-xs text-slate-400">
          Press <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-600">D</kbd> for daily entry
        </p>
      </div>
    </div>
  );
}
